/**
 * PomodoroConfig - Configuration form for the Pomodoro timer.
 *
 * Allows users to edit work duration, short break, long break, and sessions
 * before long break. All duration values are constrained between 1 and 120 minutes
 * using number inputs. Calls updateConfig() on change to persist to the store.
 *
 * @see Requirements 7.1
 */

import { useCallback } from 'react';
import { usePomodoroStore } from '@/stores';
import type { PomodoroConfig as PomodoroConfigType } from '@/types';

// ─── Constants ───────────────────────────────────────────────────────────────

const MIN_DURATION = 1;
const MAX_DURATION = 120;
const MIN_SESSIONS = 1;
const MAX_SESSIONS = 10;

// ─── Component ───────────────────────────────────────────────────────────────

export function PomodoroConfig() {
  const { config, updateConfig } = usePomodoroStore();

  const handleChange = useCallback(
    (field: keyof PomodoroConfigType, value: number) => {
      // Clamp the value to valid range
      let min = MIN_DURATION;
      let max = MAX_DURATION;

      if (field === 'sessionsBeforeLongBreak') {
        min = MIN_SESSIONS;
        max = MAX_SESSIONS;
      }

      const clamped = Math.min(max, Math.max(min, Math.round(value)));
      void updateConfig({ [field]: clamped });
    },
    [updateConfig]
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <h3 className="text-base font-semibold text-text-primary">Timer Settings</h3>

      {/* Config fields */}
      <div className="space-y-4">
        <ConfigField
          id="work-duration"
          label="Work Duration"
          value={config.workDuration}
          min={MIN_DURATION}
          max={MAX_DURATION}
          unit="min"
          onChange={(v) => handleChange('workDuration', v)}
        />

        <ConfigField
          id="short-break-duration"
          label="Short Break"
          value={config.shortBreakDuration}
          min={MIN_DURATION}
          max={MAX_DURATION}
          unit="min"
          onChange={(v) => handleChange('shortBreakDuration', v)}
        />

        <ConfigField
          id="long-break-duration"
          label="Long Break"
          value={config.longBreakDuration}
          min={MIN_DURATION}
          max={MAX_DURATION}
          unit="min"
          onChange={(v) => handleChange('longBreakDuration', v)}
        />

        <ConfigField
          id="sessions-before-long-break"
          label="Sessions Before Long Break"
          value={config.sessionsBeforeLongBreak}
          min={MIN_SESSIONS}
          max={MAX_SESSIONS}
          unit="sessions"
          onChange={(v) => handleChange('sessionsBeforeLongBreak', v)}
        />
      </div>
    </div>
  );
}

PomodoroConfig.displayName = 'PomodoroConfig';

// ─── ConfigField Sub-Component ───────────────────────────────────────────────

interface ConfigFieldProps {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  unit: string;
  onChange: (value: number) => void;
}

function ConfigField({ id, label, value, min, max, unit, onChange }: ConfigFieldProps) {
  return (
    <div className="flex items-center justify-between gap-4">
      <label
        htmlFor={id}
        className="text-sm text-text-primary font-medium"
      >
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="number"
          min={min}
          max={max}
          value={value}
          onChange={(e) => {
            const parsed = parseInt(e.target.value, 10);
            if (!isNaN(parsed)) {
              onChange(parsed);
            }
          }}
          className="
            w-16 px-2 py-1.5 text-sm text-center
            bg-bg-card border border-border rounded-lg
            text-text-primary
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring
            transition-colors duration-fast
            [appearance:textfield]
            [&::-webkit-outer-spin-button]:appearance-none
            [&::-webkit-inner-spin-button]:appearance-none
          "
          aria-label={`${label} in ${unit}`}
        />
        <span className="text-xs text-text-secondary w-12">{unit}</span>
      </div>
    </div>
  );
}
